import React, { useEffect, useMemo, useRef } from "react";
import styles from "../Graph.module.css";

export type NodeId = string;
export interface Edge {
  from: NodeId;
  to: NodeId;
}

type TraversalType = "bfs" | "dfs" | null;

interface UndirectedGraphEngineProps {
  nodes: NodeId[];
  edges: Edge[];
  traversal: TraversalType;
  startNode: NodeId | null;
  resetSignal: number;
  highlightedNodes: NodeId[];
  setHighlightedNodes: React.Dispatch<React.SetStateAction<NodeId[]>>;
  setTraversalResult: React.Dispatch<React.SetStateAction<NodeId[]>>;
}

type Adjacency = Record<NodeId, NodeId[]>;

function buildAdjacency(nodes: NodeId[], edges: Edge[]): Adjacency {
  const adj: Adjacency = {};
  for (const n of nodes) adj[n] = [];
  for (const { from, to } of edges) {
    if (!adj[from]) adj[from] = [];
    if (!adj[to]) adj[to] = [];
    if (!adj[from].includes(to)) adj[from].push(to);
    if (!adj[to].includes(from)) adj[to].push(from);
  }
  return adj;
}

function bfs(adj: Adjacency, start: NodeId): NodeId[] {
  const visited: NodeId[] = [];
  const queue: NodeId[] = [start];
  const seen = new Set<NodeId>([start]);

  while (queue.length > 0) {
    const node = queue.shift()!;
    visited.push(node);

    for (const neighbor of adj[node] ?? []) {
      if (!seen.has(neighbor)) {
        seen.add(neighbor);
        queue.push(neighbor);
      }
    }
  }
  return visited;
}

function dfs(adj: Adjacency, start: NodeId): NodeId[] {
  const visited: NodeId[] = [];
  const seen = new Set<NodeId>();

  function dfsVisit(node: NodeId) {
    seen.add(node);
    visited.push(node);

    for (const neighbor of adj[node] ?? []) {
      if (!seen.has(neighbor)) dfsVisit(neighbor);
    }
  }
  dfsVisit(start);
  return visited;
}

const UndirectedGraphEngine: React.FC<UndirectedGraphEngineProps> = ({
  nodes,
  edges,
  traversal,
  startNode,
  resetSignal,
  highlightedNodes,
  setHighlightedNodes,
  setTraversalResult,
}) => {
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const positions = useMemo(() => {
    const map: Record<NodeId, { x: number; y: number }> = {};
    if (nodes.length === 0) return map;

    const centerX = 200;
    const centerY = 130;
    const radius = 110;

    nodes.forEach((node, index) => {
      const angle = (2 * Math.PI * index) / nodes.length - Math.PI / 2;
      map[node] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });

    return map;
  }, [nodes]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => {
    clearTimers();
    setHighlightedNodes([]);
    setTraversalResult([]);

    if (!traversal || !startNode || nodes.length === 0) return;

    const adj = buildAdjacency(nodes, edges);
    const order =
      traversal === "bfs" ? bfs(adj, startNode) : dfs(adj, startNode);

    const STEP_MS = 500;

    order.forEach((nodeId, idx) => {
      const t = setTimeout(() => {
        setHighlightedNodes((prev) => [...prev, nodeId]);
        setTraversalResult((prev) => [...prev, nodeId]);
      }, idx * STEP_MS);
      timersRef.current.push(t);
    });

    return clearTimers;
  }, [
    traversal,
    nodes,
    edges,
    setHighlightedNodes,
    setTraversalResult,
  ]);

  useEffect(() => {
    clearTimers();
    setHighlightedNodes([]);
    setTraversalResult([]);
  }, [resetSignal, setHighlightedNodes, setTraversalResult]);

  if (nodes.length === 0) {
    return (
      <svg className={styles.graphSvg} viewBox="0 0 400 260" />
    );
  }

  return (
    <svg className={styles.graphSvg} viewBox="0 0 400 260">
      {edges.map((edge, idx) => {
        const p1 = positions[edge.from];
        const p2 = positions[edge.to];
        return (
          <line
            key={idx}
            x1={p1.x}
            y1={p1.y}
            x2={p2.x}
            y2={p2.y}
            className={styles.graphEdge}
          />
        );
      })}

      {nodes.map((node) => {
        const pos = positions[node];
        const isHighlighted = highlightedNodes.includes(node);

        return (
          <g key={node} transform={`translate(${pos.x}, ${pos.y})`}>
            <circle
              r={20}
              className={
                isHighlighted
                  ? `${styles.graphNode} ${styles.graphNodeHighlighted}`
                  : styles.graphNode
              }
            />
            <text dy="0.35em" className={styles.graphNodeLabel}>
              {node}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default UndirectedGraphEngine;
